<script setup>
// Assistente de IA — a base de conhecimento de um assistente.
//
// Documento entra e é processado em segundo plano, então a tela se atualiza sozinha ENQUANTO
// houver algo em processamento — e para assim que não houver (usePolling).
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import { usePolling } from '../composables/usePolling';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const route = useRoute();
const { carregando, erro, chamar } = useAssistenteIaApi();

// Reativo: trocar de assistente pela URL recarrega a tela (AIA-07, C8).
const assistenteId = computed(() => route.params.assistenteId);
const EM_PROCESSAMENTO = ['pendente', 'lendo', 'dividindo', 'vetorizando'];

const documentos = ref([]);
const respostas = ref([]);
const metaDocumentos = ref({ count: 0, current_page: 1 });
const metaRespostas = ref({ count: 0, current_page: 1 });
const paginaDocumentos = ref(1);
const paginaRespostas = ref(1);
const respostaParaApagar = ref(null);
const filtroSituacao = ref('');
const aba = ref('texto');
const novo = ref({ titulo: '', conteudo: '', url: '' });
const arquivo = ref(null);
const enviando = ref(false);
const paraApagar = ref(null);
const novaResposta = ref(null);
const buscaTexto = ref('');
const buscaItens = ref(null);
const buscando = ref(false);
// Muda toda vez que a tela é zerada (troca de assistente): invalida a busca em voo.
const buscaAtual = ref(0);

// Há processamento em curso? A pergunta NÃO pode depender do filtro da tela: com o filtro em
// "Pronto", a lista não traria os documentos em processamento e a atualização pararia justo
// quando era mais necessária (AIA-07, C4).
const temProcessando = ref(false);

// A resposta só é gravada se a tela ainda estiver pedindo a MESMA coisa.
//
// Antes, a conferência de ciclo do usePolling acontecia DEPOIS de a tarefa já ter gravado, e
// nem cobria filtro e página: o ciclo pedia "Todas", a pessoa trocava para "Erro", a resposta
// de "Todas" chegava depois e sobrescrevia a lista filtrada (AIA-08, C2).
const respostaAindaVale = (pedido, valido) =>
  pedido.situacao === filtroSituacao.value &&
  pedido.page === paginaDocumentos.value &&
  pedido.assistenteId === assistenteId.value &&
  (!valido || valido());

const carregarDocumentos = async ({ silencioso = false, valido = null } = {}) => {
  if (!assistenteId.value) return { ok: false, continuar: false };

  const pedido = {
    assistenteId: assistenteId.value,
    situacao: filtroSituacao.value,
    page: paginaDocumentos.value,
  };

  const dados = await chamar(
    assistenteIaApi.documentos(pedido.assistenteId, {
      situacao: pedido.situacao,
      page: pedido.page,
    }),
    { silencioso }
  );
  if (!dados) return { ok: false, continuar: false };
  if (!respostaAindaVale(pedido, valido)) return { ok: true, descartada: true };

  documentos.value = dados.payload;
  metaDocumentos.value = dados.meta;
  return { ok: true };
};

// Pergunta separada, que o servidor responde sem o filtro e sem a paginação da tela: só quer
// saber se sobrou algo que ainda vai mudar sozinho. A conta vem no `meta.count`.
const conferirProcessamento = async ({ silencioso = false, valido = null } = {}) => {
  if (!assistenteId.value) return { ok: false, continuar: false };

  const doAssistente = assistenteId.value;
  const dados = await chamar(
    assistenteIaApi.documentos(doAssistente, { emAndamento: true }),
    { silencioso }
  );
  if (!dados) return { ok: false, continuar: false };
  if (doAssistente !== assistenteId.value) return { ok: true, continuar: false };
  // Resposta de um ciclo já invalidado NÃO decide se a atualização continua: uma contagem velha
  // de zero, chegando depois de um "Reprocessar", mandava parar (AIA-09, C3). Quem manda parar
  // é a resposta do ciclo ATUAL.
  if (valido && !valido()) return { ok: true, continuar: temProcessando.value };

  temProcessando.value = dados.meta.count > 0;
  return { ok: true, continuar: temProcessando.value };
};

const carregarRespostas = async () => {
  if (!assistenteId.value) return;

  const pedido = { assistenteId: assistenteId.value, page: paginaRespostas.value };
  const dados = await chamar(
    assistenteIaApi.respostasProntas(pedido.assistenteId, { page: pedido.page })
  );
  if (!dados) return;
  if (pedido.assistenteId !== assistenteId.value || pedido.page !== paginaRespostas.value) return;

  respostas.value = dados.payload;
  metaRespostas.value = dados.meta;
};

const umCiclo = async (_meuCiclo, aindaVale) => {
  const lista = await carregarDocumentos({ silencioso: true, valido: aindaVale });
  if (!lista.ok) return lista;

  return conferirProcessamento({ silencioso: true, valido: aindaVale });
};

const {
  comecar: comecarPolling,
  falhou: pollingFalhou,
  invalidar: invalidarPolling,
} = usePolling(umCiclo);

// A tela ainda está montada? `atualizar()` que termina DEPOIS do desmonte chamava
// `comecarPolling()` já passado o `onBeforeUnmount(parar)`, e religava o temporizador de uma
// tela que não existe mais (AIA-10, C4).
const montado = ref(true);
onBeforeUnmount(() => {
  montado.value = false;
});

const atualizar = async () => {
  await carregarDocumentos();
  await conferirProcessamento();
  if (montado.value && temProcessando.value) comecarPolling();
};

const adicionar = async () => {
  // Antes, clicar em "Adicionar" sem escolher arquivo não fazia nada e não avisava nada
  // (AIA-07, C7).
  if (aba.value === 'pdf' && !arquivo.value) {
    erro.value = t('ASSISTENTE_IA.BASE.ESCOLHA_ARQUIVO');
    return;
  }

  enviando.value = true;

  let dados = null;
  if (aba.value === 'pdf') {
    dados = await chamar(
      assistenteIaApi.enviarPdf(assistenteId.value, arquivo.value, novo.value.titulo)
    );
  } else if (aba.value === 'url') {
    dados = await chamar(
      assistenteIaApi.criarDocumento(assistenteId.value, {
        origem: 'url',
        url: novo.value.url,
        titulo: novo.value.titulo,
      })
    );
  } else {
    dados = await chamar(
      assistenteIaApi.criarDocumento(assistenteId.value, {
        origem: 'texto',
        titulo: novo.value.titulo,
        conteudo: novo.value.conteudo,
      })
    );
  }

  enviando.value = false;
  if (!dados) return;

  novo.value = { titulo: '', conteudo: '', url: '' };
  limparArquivo();
  paginaDocumentos.value = 1;
  invalidarPolling();
  atualizar();
  conferirTamanhoDaBase();
};

const campoDeArquivo = ref(null);

const escolherArquivo = evento => {
  arquivo.value = evento.target.files[0];
};

// Sem limpar o campo, ele continuava mostrando o nome do arquivo já enviado e o segundo
// "Adicionar" mandava o mesmo PDF de novo (AIA-07, C7).
const limparArquivo = () => {
  arquivo.value = null;
  if (campoDeArquivo.value) campoDeArquivo.value.value = '';
};

const reprocessar = async documento => {
  const dados = await chamar(
    assistenteIaApi.reprocessarDocumento(assistenteId.value, documento.id)
  );
  if (!dados) return;

  // Sem invalidar, a resposta do ciclo em voo (a contagem de ANTES do reprocessar, zero)
  // chegava depois e mandava a atualização PARAR — justo quando ela voltou a ser necessária
  // (AIA-09, C3).
  invalidarPolling();
  atualizar();
};

const apagar = async () => {
  const alvo = paraApagar.value;
  const eraOUltimoDaPagina = documentos.value.length === 1;
  paraApagar.value = null;
  if (!alvo) return;

  const feito = await chamar(assistenteIaApi.apagarDocumento(assistenteId.value, alvo.id));
  if (feito === null) return;

  // Apagar o único item de uma página deixava a pessoa numa página vazia, sem nada para
  // clicar a não ser "Anterior" (AIA-08, C4).
  if (eraOUltimoDaPagina && paginaDocumentos.value > 1) paginaDocumentos.value -= 1;
  invalidarPolling();
  atualizar();
  conferirTamanhoDaBase();
};

const salvarResposta = async () => {
  const resposta = novaResposta.value;
  const dados = resposta.id
    ? await chamar(
        assistenteIaApi.editarRespostaPronta(assistenteId.value, resposta.id, resposta)
      )
    : await chamar(assistenteIaApi.criarRespostaPronta(assistenteId.value, resposta));

  if (!dados) return;
  novaResposta.value = null;
  carregarRespostas();
};

// Apagar resposta pronta passou a pedir confirmação, como as outras ações destrutivas do
// módulo — antes sumia no primeiro clique, sem volta (AIA-07, C3).
const apagarResposta = async () => {
  const alvo = respostaParaApagar.value;
  const eraAUltimaDaPagina = respostas.value.length === 1;
  respostaParaApagar.value = null;
  if (!alvo) return;

  const feito = await chamar(
    assistenteIaApi.apagarRespostaPronta(assistenteId.value, alvo.id)
  );
  if (feito === null) return;

  // Mesma regra dos documentos: apagar o único item de uma página deixava a pessoa numa página
  // vazia. O relatório da AIA-08 disse "os cinco" e este ficou de fora (AIA-09, C3).
  if (eraAUltimaDaPagina && paginaRespostas.value > 1) paginaRespostas.value -= 1;
  carregarRespostas();
};

const testarBusca = async () => {
  // Um número por BUSCA, não o id do assistente: A → B → A devolve o mesmo id, e a resposta
  // velha voltava para a tela. E o `buscando` é desligado ANTES do descarte — com o `return`
  // vindo primeiro, o spinner ficava preso para sempre (AIA-10, C4).
  const destaBusca = buscaAtual.value;
  buscando.value = true;

  const dados = await chamar(assistenteIaApi.buscarNaBase(assistenteId.value, buscaTexto.value));

  if (destaBusca !== buscaAtual.value) return;

  buscando.value = false;
  if (dados) buscaItens.value = dados.payload.itens;
};

const nomeDaSituacao = situacao =>
  t(`ASSISTENTE_IA.BASE.SITUACOES.${situacao.toUpperCase()}`);

// 25 é o que o controller devolve por página (POR_PAGINA). Sem esta conta, o botão "Próxima"
// ficava sempre habilitado e a última página levava a uma lista vazia (AIA-08, C4).
const POR_PAGINA = 25;

const temProximaPaginaDeDocumentos = computed(
  () => paginaDocumentos.value * POR_PAGINA < metaDocumentos.value.count
);
const temProximaPaginaDeRespostas = computed(
  () => paginaRespostas.value * POR_PAGINA < metaRespostas.value.count
);

const irParaPaginaDeDocumentos = pagina => {
  paginaDocumentos.value = pagina;
  invalidarPolling();
  carregarDocumentos();
};

const irParaPaginaDeRespostas = pagina => {
  paginaRespostas.value = pagina;
  carregarRespostas();
};

// Trocar o filtro volta para a página 1 — estar na página 3 de "Todas" não diz nada sobre
// quantas páginas "Erro" tem — e invalida o ciclo em curso (AIA-08, C2 e C4).
const trocarFiltro = async valor => {
  filtroSituacao.value = valor;
  paginaDocumentos.value = 1;
  invalidarPolling();
  await atualizar();
};

// Trocar de aba limpa o arquivo escolhido: sem isso, voltar para "pdf" mostrava o nome do
// arquivo já enviado e "Adicionar" mandava o mesmo PDF de novo (AIA-08, C4).
const trocarAba = tipo => {
  aba.value = tipo;
  limparArquivo();
};

const limparTelaDoAssistente = () => {
  documentos.value = [];
  respostas.value = [];
  metaDocumentos.value = { count: 0, current_page: 1 };
  metaRespostas.value = { count: 0, current_page: 1 };
  paginaDocumentos.value = 1;
  paginaRespostas.value = 1;
  paraApagar.value = null;
  respostaParaApagar.value = null;
  novaResposta.value = null;
  buscaTexto.value = '';
  buscaItens.value = null;
  buscaAtual.value += 1;
  buscando.value = false;
  baseGrande.value = null;
  filtroSituacao.value = '';
  temProcessando.value = false;
  novo.value = { titulo: '', conteudo: '', url: '' };
  limparArquivo();
};

// Acima de um certo tamanho de base, a busca deixa de ser exata e passa a usar o indice
// aproximado — que pode devolver menos trechos do que existem (AIA-13, A4). Quem opera precisa
// ver isso na tela da base, que e onde a base cresce.
const baseGrande = ref(null);

// A consulta e SILENCIOSA (AIA-14, A1): este aviso e um extra da tela, e uma falha dele nao
// pode virar a faixa vermelha de erro por cima do que a pessoa esta fazendo na base.
//
// E ela acompanha a tela: zera ao trocar de assistente (senao o aviso do anterior fica) e roda
// de novo depois de adicionar ou apagar documento, que e quando a base cruza o limite.
const conferirTamanhoDaBase = async () => {
  const pedidoDe = assistenteId.value;
  const estado = await chamar(assistenteIaApi.estado(), { silencioso: true });
  if (!estado || pedidoDe !== assistenteId.value) return;

  const avisos = estado.payload?.assistentes_com_base_grande || [];
  baseGrande.value =
    avisos.find(aviso => String(aviso.id) === String(pedidoDe)) || null;
};

onMounted(async () => {
  await atualizar();
  carregarRespostas();
  conferirTamanhoDaBase();
});

// Ao SAIR da rota, o vue-router zera os params antes de destruir a tela, e o watch disparava
// com o id vazio: saía um GET /assistentes/undefined/documentos a cada troca de tela.
//
// E a limpeza passou a ser de TUDO: sobravam a confirmação de apagar, o rascunho de resposta
// pronta, a busca e o arquivo escolhido do assistente anterior (AIA-08, C4).
watch(assistenteId, id => {
  invalidarPolling();
  limparTelaDoAssistente();
  if (!id) return;

  atualizar();
  carregarRespostas();
  conferirTamanhoDaBase();
});
</script>

<template>
  <section class="flex flex-col gap-6">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>
    <Banner v-if="pollingFalhou" color="amber" :action-label="t('ASSISTENTE_IA.COMUM.ATUALIZAR')" @action="atualizar">
      {{ t('ASSISTENTE_IA.BASE.ATUALIZACAO_PAROU') }}
    </Banner>
    <Banner v-if="baseGrande" color="amber">
      {{
        t('ASSISTENTE_IA.BASE.BASE_GRANDE', {
          trechos: baseGrande.trechos,
          limite: baseGrande.limite,
        })
      }}
    </Banner>

    <div class="flex items-center justify-between">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.BASE.TITULO') }}
      </h2>
      <Button
        :label="t('ASSISTENTE_IA.COMUM.ATUALIZAR')"
        variant="faded"
        color="slate"
        size="xs"
        @click="atualizar"
      />
    </div>

    <!-- Adicionar documento -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1">
      <div class="flex gap-2">
        <button
          v-for="tipo in ['texto', 'url', 'pdf']"
          :key="tipo"
          class="px-3 py-1 text-sm rounded-lg"
          :class="
            aba === tipo ? 'bg-n-brand text-white' : 'bg-n-slate-3 text-n-slate-11'
          "
          @click="trocarAba(tipo)"
        >
          {{
            tipo === 'texto'
              ? t('ASSISTENTE_IA.BASE.TEXTO')
              : tipo === 'url'
                ? t('ASSISTENTE_IA.BASE.PAGINA')
                : t('ASSISTENTE_IA.BASE.PDF')
          }}
        </button>
      </div>

      <Input v-model="novo.titulo" :label="t('ASSISTENTE_IA.BASE.TITULO_CAMPO')" />

      <label v-if="aba === 'texto'" class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.BASE.CONTEUDO') }}
        <textarea
          v-model="novo.conteudo"
          rows="6"
          class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
        />
      </label>

      <Input
        v-if="aba === 'url'"
        v-model="novo.url"
        :label="t('ASSISTENTE_IA.BASE.URL')"
        :message="t('ASSISTENTE_IA.BASE.URL_AJUDA')"
      />

      <label v-if="aba === 'pdf'" class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ASSISTENTE_IA.BASE.ARQUIVO') }}
        <input ref="campoDeArquivo" type="file" accept="application/pdf" @change="escolherArquivo" />
        <span class="text-n-slate-11">{{ t('ASSISTENTE_IA.BASE.ARQUIVO_AJUDA') }}</span>
      </label>

      <div>
        <Button
          :label="t('ASSISTENTE_IA.BASE.ADICIONAR')"
          :is-loading="enviando"
          size="sm"
          @click="adicionar"
        />
      </div>
    </div>

    <!-- Documentos -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.BASE.DOCUMENTOS') }}
        </h3>
        <select
          :value="filtroSituacao"
          class="px-3 py-1.5 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
          @change="trocarFiltro($event.target.value)"
        >
          <option value="">{{ t('ASSISTENTE_IA.BASE.TODAS') }}</option>
          <option value="pronto">{{ t('ASSISTENTE_IA.BASE.SITUACOES.PRONTO') }}</option>
          <option value="erro">{{ t('ASSISTENTE_IA.BASE.SITUACOES.ERRO') }}</option>
          <option value="pendente">{{ t('ASSISTENTE_IA.BASE.SITUACOES.PENDENTE') }}</option>
        </select>
      </div>

      <div v-if="carregando && !documentos.length" class="flex justify-center py-10">
        <Spinner />
      </div>

      <TabelaVazia
        v-else-if="!documentos.length"
        :mensagem="t('ASSISTENTE_IA.BASE.VAZIO')"
      />

      <table v-else class="w-full text-sm text-left">
        <thead class="border-b text-n-slate-11 border-n-weak">
          <tr>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.TITULO_CAMPO') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.ORIGEM') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.SITUACAO') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.TRECHOS') }}</th>
            <th class="py-2 font-medium text-right">
              {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="documento in documentos" :key="documento.id" class="border-b border-n-weak">
            <td class="py-3 text-n-slate-12">
              {{ documento.titulo }}
              <div v-if="documento.erro" class="text-n-ruby-11" :title="documento.erro">
                {{ documento.erro }}
              </div>
            </td>
            <td class="py-3 text-n-slate-11">{{ documento.origem }}</td>
            <td class="py-3">
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="{
                  'bg-n-teal-3 text-n-teal-11': documento.situacao === 'pronto',
                  'bg-n-ruby-3 text-n-ruby-11': documento.situacao === 'erro',
                  'bg-n-amber-3 text-n-amber-11': EM_PROCESSAMENTO.includes(documento.situacao),
                }"
              >
                {{ nomeDaSituacao(documento.situacao) }}
              </span>
            </td>
            <td class="py-3 text-n-slate-11">{{ documento.trechos }}</td>
            <td class="py-3">
              <div class="flex justify-end gap-2">
                <button
                  class="text-n-blue-11 hover:underline"
                  @click="reprocessar(documento)"
                >
                  {{ t('ASSISTENTE_IA.BASE.REPROCESSAR') }}
                </button>
                <button
                  class="text-n-ruby-11 hover:underline"
                  @click="paraApagar = documento"
                >
                  {{ t('ASSISTENTE_IA.COMUM.APAGAR') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- A API pagina de 25 em 25: sem isto, do 26o documento em diante o administrador nao
           via, nao reprocessava e nao apagava (AIA-07, C2). -->
      <div v-if="metaDocumentos.count > documentos.length" class="flex items-center gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.ANTERIOR')"
          variant="faded"
          color="slate"
          size="xs"
          :disabled="paginaDocumentos <= 1"
          @click="irParaPaginaDeDocumentos(paginaDocumentos - 1)"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('ASSISTENTE_IA.COMUM.PAGINA') }} {{ metaDocumentos.current_page }}
        </span>
        <Button
          :label="t('ASSISTENTE_IA.COMUM.PROXIMA')"
          variant="faded"
          color="slate"
          size="xs"
          :disabled="!temProximaPaginaDeDocumentos"
          @click="irParaPaginaDeDocumentos(paginaDocumentos + 1)"
        />
      </div>

      <div
        v-if="paraApagar"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-4 bg-n-ruby-2"
      >
        <p class="mb-0 font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.BASE.APAGAR_TITULO') }}
        </p>
        <p class="mb-0 text-n-slate-11">{{ t('ASSISTENTE_IA.BASE.APAGAR_TEXTO') }}</p>
        <div class="flex gap-2">
          <Button
            :label="t('ASSISTENTE_IA.COMUM.APAGAR')"
            color="ruby"
            size="sm"
            @click="apagar"
          />
          <Button
            :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
            variant="faded"
            color="slate"
            size="sm"
            @click="paraApagar = null"
          />
        </div>
      </div>
    </div>

    <!-- Respostas prontas -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.BASE.RESPOSTAS') }}
        </h3>
        <Button
          :label="t('ASSISTENTE_IA.BASE.NOVA_RESPOSTA')"
          variant="faded"
          color="slate"
          size="xs"
          @click="novaResposta = { pergunta: '', resposta: '', aprovada: true }"
        />
      </div>

      <div
        v-if="novaResposta"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <Input
          v-model="novaResposta.pergunta"
          :label="t('ASSISTENTE_IA.BASE.PERGUNTA')"
        />
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('ASSISTENTE_IA.BASE.RESPOSTA') }}
          <textarea
            v-model="novaResposta.resposta"
            rows="4"
            class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-black2"
          />
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="novaResposta.aprovada" type="checkbox" />
          {{ t('ASSISTENTE_IA.BASE.APROVADA') }}
        </label>
        <div class="flex gap-2">
          <Button :label="t('ASSISTENTE_IA.COMUM.SALVAR')" size="sm" @click="salvarResposta" />
          <Button
            :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
            variant="faded"
            color="slate"
            size="sm"
            @click="novaResposta = null"
          />
        </div>
      </div>

      <TabelaVazia
        v-if="!respostas.length"
        :mensagem="t('ASSISTENTE_IA.BASE.RESPOSTAS_VAZIO')"
      />

      <table v-else class="w-full text-sm text-left">
        <thead class="border-b text-n-slate-11 border-n-weak">
          <tr>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.PERGUNTA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.RESPOSTA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.APROVADA') }}</th>
            <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.BASE.VETORIZADA') }}</th>
            <th class="py-2 font-medium text-right">
              {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="resposta in respostas" :key="resposta.id" class="border-b border-n-weak">
            <td class="py-3 text-n-slate-12">{{ resposta.pergunta }}</td>
            <td class="py-3 text-n-slate-11">{{ resposta.resposta }}</td>
            <td class="py-3 text-n-slate-11">{{ resposta.aprovada ? '✓' : '—' }}</td>
            <td class="py-3 text-n-slate-11">{{ resposta.vetorizada ? '✓' : '—' }}</td>
            <td class="py-3">
              <div class="flex justify-end gap-2">
                <button
                  class="text-n-blue-11 hover:underline"
                  @click="novaResposta = { ...resposta }"
                >
                  {{ t('ASSISTENTE_IA.COMUM.EDITAR') }}
                </button>
                <button
                  class="text-n-ruby-11 hover:underline"
                  @click="respostaParaApagar = resposta"
                >
                  {{ t('ASSISTENTE_IA.COMUM.APAGAR') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- paginacao das respostas prontas (AIA-07, C2) -->
      <div v-if="metaRespostas.count > respostas.length" class="flex items-center gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.ANTERIOR')"
          variant="faded"
          color="slate"
          size="xs"
          :disabled="paginaRespostas <= 1"
          @click="irParaPaginaDeRespostas(paginaRespostas - 1)"
        />
        <span class="text-sm text-n-slate-11">
          {{ t('ASSISTENTE_IA.COMUM.PAGINA') }} {{ metaRespostas.current_page }}
        </span>
        <Button
          :label="t('ASSISTENTE_IA.COMUM.PROXIMA')"
          variant="faded"
          color="slate"
          size="xs"
          :disabled="!temProximaPaginaDeRespostas"
          @click="irParaPaginaDeRespostas(paginaRespostas + 1)"
        />
      </div>

      <div
        v-if="respostaParaApagar"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-ruby-4 bg-n-ruby-2"
      >
        <p class="mb-0 font-medium text-n-slate-12">
          {{ t('ASSISTENTE_IA.BASE.APAGAR_RESPOSTA_TITULO') }}
        </p>
        <p class="mb-0 text-n-slate-11">
          {{ t('ASSISTENTE_IA.BASE.APAGAR_RESPOSTA_TEXTO') }}
        </p>
        <div class="flex gap-2">
          <Button
            :label="t('ASSISTENTE_IA.COMUM.APAGAR')"
            color="ruby"
            size="sm"
            @click="apagarResposta"
          />
          <Button
            :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
            variant="faded"
            color="slate"
            size="sm"
            @click="respostaParaApagar = null"
          />
        </div>
      </div>
    </div>
    <!-- Testar a busca -->
    <div class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTENTE_IA.BASE.TESTAR_BUSCA') }}
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('ASSISTENTE_IA.BASE.TESTAR_BUSCA_AJUDA') }}
      </p>

      <div class="flex gap-2">
        <Input v-model="buscaTexto" class="flex-1" />
        <Button
          :label="t('ASSISTENTE_IA.BASE.TESTAR_BUSCA')"
          :is-loading="buscando"
          size="sm"
          @click="testarBusca"
        />
      </div>

      <div v-if="buscaItens" class="flex flex-col gap-2">
        <p v-if="!buscaItens.length" class="mb-0 text-sm text-n-amber-11">
          {{ t('ASSISTENTE_IA.BASE.NADA_ENCONTRADO') }}
        </p>
        <div
          v-for="item in buscaItens"
          :key="`${item.tipo}-${item.id}`"
          class="p-3 text-sm border rounded-lg border-n-weak"
        >
          <div class="flex justify-between text-n-slate-11">
            <span>{{ item.titulo || item.pergunta }}</span>
            <span>{{ t('ASSISTENTE_IA.BASE.SIMILARIDADE') }}: {{ item.similaridade }}</span>
          </div>
          <p class="mb-0 text-n-slate-12">{{ item.conteudo }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
